import AssetLoader from "@/components/AssetLoader/AssetLoader";
import { AssetTypes } from "@/model/AssetTypes";
import { ObjectId } from "@/model/db/General";
import BFPlaceholder from "@/modules/abstract-ui/general/Placeholder/BFPlaceholder";
import BFOverlay from "@/modules/abstract-ui/general/whisper/BFOverlay";
import { hasValue } from "@/utils/Helpers";
import moment from "moment";
import i18n from "../../../../../i18n";
import BFStatus from "../../../../../modules/abstract-ui/data/status/BFStatus";
import StringUtils from "../../../../../utils/StringUtils";
import { Invoice, InvoiceKind, InvoicePayment } from "../../RAInterfaces";
import RAInvoicePaymentCheckInfo from "../PaymentList/RAInvoicePaymentCheckInfo";
import "./InvoicePaymentPlan.scss";

interface LinkedInvoicePaymentPlanProps {
  asset: Invoice;
  type: "payment" | "installment";
  size?: "default" | "sm" | "xs" | "xxs";
}
const InvoicePaymentPlan = (props: LinkedInvoicePaymentPlanProps) => {
  if (props.asset?.data.kind === InvoiceKind.INVOICE) {
    const show =
      (props.asset?.data[props.type] || []).length >
      (props.type === "payment" ? 1 : 0);
    return (
      <>
        {show && (
          <BFOverlay
            enterable
            delay={0}
            trigger="hover"
            speaker={
              <div className={`ra-invoice-entry-suffix-payments padding-10`}>
                <InvoicePaymentDataTable {...props} />
              </div>
            }
          >
            <div className={`invoice-payment-plan`}>
              <BFStatus
                size={props.size || "xs"}
                label={
                  props.type === "payment"
                    ? i18n.t(
                        "ra:Invoice.countPaymentsPaid",
                        "{{payed}}/{{count}} Zahlungen bezahlt",
                        {
                          payed: props.asset?.data[props.type].filter(
                            (e) => e.payedDate
                          ).length,
                          count: props.asset?.data[props.type].length,
                        }
                      )
                    : i18n.t(
                        "ra:Invoice.countInstallmentsPaid",
                        "{{payed}}/{{count}} Abschläge bezahlt",
                        {
                          payed: props.asset?.data[props.type].filter(
                            (e) => e.payedDate
                          ).length,
                          count: props.asset?.data[props.type].length,
                        }
                      )
                }
              />
            </div>
          </BFOverlay>
        )}
      </>
    );
  } else {
    return null;
  }
};

export default InvoicePaymentPlan;

export const InvoicePaymentDataTable = (
  props: LinkedInvoicePaymentPlanProps & {
    highlightedPayments?: ObjectId[];
    withTitle?: boolean;
    className?: string;
  }
) => {
  const show =
    (props.asset?.data[props.type] || []).length >
    (props.type === "payment" ? 1 : 0);
  if (!show) {
    return null;
  }
  return (
    <div className={props.className}>
      {props.withTitle && (
        <div className={`__title __h3`}>
          {props.type === "payment"
            ? i18n.t("ra:Invoice.payment.title", "Zahlungen")
            : i18n.t("ra:Invoice.installment.title", "Abschläge")}
        </div>
      )}
      <table className={`invoice-payment-data-table`}>
        <thead>
          <tr>
            <th>{i18n.t("ra:Invoice.payment.dueDate", "Fällig")}</th>
            <th className={`amount`}>
              {i18n.t("ra:Invoice.payment.amount", "Betrag")}
            </th>
            <th className={`status`}>
              {i18n.t("ra:Invoice.payment.status", "Status")}
            </th>
            <th className={`check-status`}>
              {i18n.t("ra:Invoice.payment.checking", "Freigabe")}
            </th>
            <th className={`pay-status`}>
              {i18n.t("ra:Invoice.payment.payStatus", "Bezahlt")}
            </th>
          </tr>
        </thead>
        <tbody>
          {props.asset.data[props.type].map((payment, i) => {
            let color = "#cccccc";
            let label = i18n.t("ra:Invoice.payment.isFuture", "Zukünftig");
            const paymentDueDate = moment(payment.date);
            const isPaid = Boolean(payment.payedDate);

            if (isPaid) {
              color = "#6bd33c";
              label =
                i18n.t("ra:Invoice.payment.isPaid", "Bezahlt") +
                " " +
                StringUtils.formatDate(payment.payedDate);
            } else {
              const overdueBoundary = moment().add(3, "days");
              if (paymentDueDate.isBefore(moment(), "day")) {
                color = "#df5858";
                label = i18n.t("ra:Invoice.payment.isOverdue", "Überfällig");
              } else if (paymentDueDate.isBefore(overdueBoundary, "day")) {
                color = "#df8c58";
                label = i18n.t("ra:Invoice.payment.isDue", "Fällig");
              }
            }

            return (
              <tr
                key={i}
                className={`payment-entry ${
                  props.highlightedPayments?.includes(payment.id)
                    ? `__highlighted`
                    : ``
                }`}
              >
                <td className={`payment-entry-date`}>
                  {StringUtils.formatDate(payment.date)}
                </td>
                <td className={`amount`}>
                  {StringUtils.formatCurrency(
                    payment.amount,
                    undefined,
                    undefined,
                    props.asset?.data.invoice.value.currency
                  )}
                </td>
                <td className={`status`}>
                  <BFStatus size={"xs"} label={label} color={color} />
                </td>
                {hasValue(payment.linkedPaymentObject) ? (
                  <>
                    <td className={`check-status`}>
                      <AssetLoader
                        assetType={AssetTypes.Invoicing.Payment}
                        id={payment.linkedPaymentObject}
                        renderLoading={() => (
                          <BFPlaceholder
                            width={100}
                            rows={1}
                            minHeight={18}
                            loading
                          >
                            <div />
                          </BFPlaceholder>
                        )}
                        renderError={() => (
                          <div className={`__error`}>
                            {i18n.t(
                              "ra:Invoice.payment.errorLoading",
                              "Fehler beim Laden"
                            )}
                          </div>
                        )}
                        render={(linkedPayment: InvoicePayment) => {
                          return (
                            <div className={`__text-ellipsis`}>
                              <RAInvoicePaymentCheckInfo
                                payment={linkedPayment}
                              />
                            </div>
                          );
                        }}
                      />
                      {/* <BFStatus size={"xs"} label={label} color={color} /> */}
                    </td>
                    <td className={`pay-status`}>
                      {payment.payedDate ? (
                        StringUtils.formatDate(payment.payedDate)
                      ) : (
                        <span className={`__sub-text`}>
                          {i18n.t(
                            "ra:Invoice.payment.notPaid",
                            "Nicht bezahlt"
                          )}
                        </span>
                      )}
                    </td>
                  </>
                ) : (
                  <td colSpan={2} className={`check-status __sub-text`}>
                    {i18n.t(
                      "ra:Invoice.payment.noPaymentObject",
                      "Kein Zahlung initiiert"
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
